import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { Checkbox, Contact } from "./FieldComponents";
import { CreateContact } from "./CreateContact";
import { CreateLead } from "./CreateLead";

export const CreateCase = ({ user, setCreateCase, fetchCases, caseTemplates }) => {
    const [selectedContact, setSelectedContact] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    const [marketingSources, setMarketingSources] = useState([]);
    const [caseData, setCaseData] = useState({
        case_name: "",
        contact_id: 0,
        lead_id: null,
        template_id: 1,
        is_parent_case: false,
        marketing_source_id: ""
    });
    const [createContact, setCreateContact] = useState(false);
    const [createLead, setCreateLead] = useState(false);
    const [leadOrContact, setLeadOrContact] = useState("contact");

    useEffect(() => {
        if (caseData.is_parent_case) {
            return;
        }

        setCaseData((prevData) => ({
            ...prevData,
            contact_id: Number(selectedContact?.contact_id || selectedContact?.id) || null,
            lead_id: selectedContact?.contact_id ? Number(selectedContact.id) : null
        }));
    }, [selectedContact, caseData.is_parent_case]);

    useEffect(() => {
        const fetchMarketingSources = async () => {
            try {
                const response = await fetch(`https://api.casedb.co/marketing_sources.php?time=${Date.now()}`, {
                    credentials: 'include'
                });
                const data = await response.json();
                setMarketingSources(Array.isArray(data?.marketing_sources) ? data.marketing_sources : []);
            } catch (error) {
                console.error('Error fetching marketing sources:', error);
            }
        };

        fetchMarketingSources();
    }, []);

    const handleFieldChange = (field, value) => {
        setCaseData((prevData) => ({
            ...prevData,
            [field]: value
        }));
    };

    const handleNewContactCreated = (contact) => {
        setSelectedContact(contact);
        setCreateContact(false);
        setCreateCase(true);
    };

    const handleCreateCase = async () => {
        try {
            if (!caseData.case_name?.trim()) {
                window.alert('Case name is required.');
                return false;
            }

            if (caseData.is_parent_case && !caseData.marketing_source_id) {
                window.alert('Marketing Source is required for a parent case.');
                return false;
            }

            const response = await fetch("https://api.casedb.co/cases.php", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(caseData),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            if (!data?.success) {
                throw new Error(data?.message || 'Failed to create case');
            }
            fetchCases();
            return true;

        } catch (error) {
            console.error("Error creating case:", error);
            window.alert(error?.message || 'Error creating case');
            return false;
        }
    };

    const handleNewLeadCreated = (lead) => {
        setSelectedLead(lead);
        setCreateLead(false);
        setCreateCase(true);
    };

    return (
        <>
            <Modal 
                title="Create Case" 
                onClose={() => setCreateCase(false)}
                header={(
                    <div className='modal-header-actions'>
                        <button className='action alt' onClick={() => setCreateCase(false)}>Cancel</button>
                        <button className='action' onClick={async () => {
                            const success = await handleCreateCase();
                            if (success) {
                                setCreateCase(false);
                            }
                        }}>Save</button>
                    </div>
                )}
            >
                <div className='modal-content-wrapper case-create-modal'>
                    <div className='form-group'>
                        <label htmlFor='case_name' className='subtext'>Case Name<span className='required'>*</span></label>
                        <input
                            id='case_name'
                            name='case_name'
                            type="text"
                            placeholder="Case Name"
                            value={caseData.case_name}
                            onChange={(e) => handleFieldChange("case_name", e.target.value)}
                        />
                    </div>
                    <div className='form-group'>
                        <Checkbox
                            value={caseData.is_parent_case}
                            onChange={() => {
                                const checked = !caseData.is_parent_case;
                                setCaseData((prevData) => ({
                                    ...prevData,
                                    is_parent_case: checked,
                                    marketing_source_id: checked ? prevData.marketing_source_id : '',
                                    contact_id: checked ? null : prevData.contact_id,
                                    lead_id: checked ? null : prevData.lead_id
                                }));
                                if (checked) {
                                    setSelectedContact(null);
                                }
                            }}
                            label="Parent Case"
                        />
                    </div>
                    {caseData.is_parent_case ? (
                        <div className='form-group'>
                            <label htmlFor='marketing_source_id' className='subtext'>Marketing Source<span className='required'>*</span></label>
                            <div className='select-wrapper' style={{ width: '100%' }}>
                                <select
                                    id='marketing_source_id'
                                    className='filter-select'
                                    style={{ width: '100%' }}
                                    value={caseData.marketing_source_id}
                                    onChange={(e) => handleFieldChange('marketing_source_id', e.target.value)}
                                >
                                    <option value=''>Select...</option>
                                    {marketingSources.map((source) => (
                                        <option key={source.id} value={source.id}>{source.source_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className='form-group'>
                            <label htmlFor='lead_id' className='subtext'>Link or Create Lead<span className='required'>*</span></label>
                            <Contact
                                setSelectedContact={setSelectedContact}
                                selectedContact={selectedContact}
                                onCreateNewLead={() => setCreateLead(true)}
                                lead={true}
                            />
                        </div>
                    )}
                </div>
            </Modal>
            {createContact && <CreateContact setCreateContact={setCreateContact} onContactCreated={handleNewContactCreated} />}
            {createLead && <CreateLead setCreateLead={setCreateLead} user={user} />}
        </>
    );
};